import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractServerMessage } from '@/features/item-panel/api/extractServerMessage';
import type { DependencyTabAdapter } from '@/features/item-panel/components/adapters/types';
import type {
  AddFlow,
  DependencySectionValue,
  RemoveFlow,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';
import {
  dependencyPairFor,
  isParentChildRejection,
} from '@/features/item-panel/components/tabs/dependencies/lib/utils';
import {
  InvalidDependencyPairError,
  ReadOnlyAdapterError,
  SelfDependencyError,
} from '@/features/items/mutations/dependencyErrors';

interface DependencyMutationFlows {
  addFlow: AddFlow;
  removeFlow: RemoveFlow;
  openAdd: (section: DependencySectionValue) => void;
  cancelAdd: () => void;
  add: (section: DependencySectionValue, pickedItemId: number) => Promise<void>;
  remove: (section: DependencySectionValue, rowItemId: number) => Promise<void>;
}

export const useDependencyMutationFlows = (
  adapter: DependencyTabAdapter,
  currentItemId: number,
): DependencyMutationFlows => {
  const { t } = useTranslation();
  const [addFlow, setAddFlow] = useState<AddFlow>({ kind: 'idle' });
  const [removeFlow, setRemoveFlow] = useState<RemoveFlow>({ kind: 'idle' });

  const validationMessage = (error: unknown): string | null => {
    if (error instanceof SelfDependencyError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.selfDependencyError);
    }

    if (error instanceof InvalidDependencyPairError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.invalidDependencyPairError);
    }

    if (error instanceof ReadOnlyAdapterError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.readOnlyError);
    }

    return null;
  };

  const add = async (section: DependencySectionValue, pickedItemId: number): Promise<void> => {
    const [predecessorId, successorId] = dependencyPairFor(section, currentItemId, pickedItemId);
    setAddFlow({ kind: 'submitting', section });
    try {
      await adapter.addDependency(predecessorId, successorId);
      setAddFlow({ kind: 'idle' });
    } catch (error) {
      const serverMessage = await extractServerMessage(error);
      const message =
        validationMessage(error) ??
        (isParentChildRejection(serverMessage)
          ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.parentChildError)
          : serverMessage) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addError);
      setAddFlow({ kind: 'error', section, message });
    }
  };

  const remove = async (section: DependencySectionValue, rowItemId: number): Promise<void> => {
    if (removeFlow.kind === 'submitting') return;
    const [predecessorId, successorId] = dependencyPairFor(section, currentItemId, rowItemId);
    setRemoveFlow({ kind: 'submitting', section, itemId: rowItemId });
    try {
      await adapter.removeDependency(predecessorId, successorId);
      setRemoveFlow({ kind: 'idle' });
    } catch (error) {
      const message =
        validationMessage(error) ??
        (await extractServerMessage(error)) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.removeError);
      setRemoveFlow({ kind: 'error', section, itemId: rowItemId, message });
    }
  };

  return {
    addFlow,
    removeFlow,
    openAdd: (section) => setAddFlow({ kind: 'open', section }),
    cancelAdd: () => setAddFlow({ kind: 'idle' }),
    add,
    remove,
  };
};
